// @ts-nocheck
import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Ref from 'models/propTypes/ref';

import useTracking from 'hooks/analytics';
import { getPageValues } from 'support/analytics/analyticsHelper';

import HomeRow from 'components/pages/home/HomeRow';
import ListRows from 'components/common/ListRows';

import FocusGroup from 'components/focus/FocusGroup';
import { useCurrentVideo } from 'hooks/video';

import { exitFullScreenPlayer } from 'actions/hero';

import styles from './DiscoveryRowOverlay.module.scss';

const messages = {
  overlayBugLive: {
    id: 'playerOverlay.bug.live',
    description: 'Live Bug Text',
  },
  ttsLive: {
    id: 'playerOverlay.tts.live',
    description: 'TTS for Live Bug',
  },
  ttsEndOfNavigation: {
    id: 'navigation.tts.ttsEndOfNavigation',
    description: 'TTS text for navigation command cannot be executed',
  },
};

const FocusId = {
  DISCOVERY_ROW: 'discovery-row',
  FOCUS_HOLDER: 'focus-holder',
};

const DiscoveryRowOverlay = ({
  onDiscoveryRowFocusIndexChanged,
  firstRowRef,
  rowsRef,
  isOverlayDiscoveryVisible,
  onAction,
  hideDiscoveryOverlayonClick,
}) => {
  const [currentVideo] = useCurrentVideo();

  const dispatch = useDispatch();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const videoParam = params.get('video');
  const isLoadingNewVideo = videoParam
    ? videoParam !== currentVideo?.id
    : false;

  const rowsData = useSelector(state => state.kitchenSink?.data?.rows);
  const liveRowData = rowsData?.filter(row => row.slug === 'live');

  const liveRow = [...liveRowData];

  // filter out p+ premium only streams from discovery row
  // include stream if it doesn't require p+ login or (if it does) only include essentials enabled streams
  const dataFiltered = liveRowData[0].data.filter(
    item =>
      item.authenticationDetails?.paramountplus === null ||
      item.authenticationDetails?.paramountplus?.essentials,
  );

  let liveRowFiltered = liveRowData[0];
  liveRowFiltered = { ...liveRowFiltered, data: dataFiltered };

  liveRow[0] = liveRowFiltered;

  const ttsEndOfNavigation = useIntl().formatMessage(
    messages.ttsEndOfNavigation,
  );

  const { trackEvent } = useTracking();
  const history = useHistory();
  const shouldSnapRowToTop = useCallback(() => true, []);

  const [isCurrentlyVisible, setIsCurrentlyVisible] = useState(
    isOverlayDiscoveryVisible,
  );
  if (isCurrentlyVisible !== isOverlayDiscoveryVisible) {
    setIsCurrentlyVisible(isOverlayDiscoveryVisible);
    if (isOverlayDiscoveryVisible) {
      trackEvent('playback interaction', {
        ...getPageValues('main'),
        pageType: 'main',
        moduleName: 'discovery row',
        moduleLocation: 'On Now',
        moduleAction: 'expand',
        moduleCampaign: 'video discovery row',
      });
    } else {
      trackEvent('playback interaction', {
        ...getPageValues('main'),
        pageType: 'main',
        moduleName: 'discovery row',
        moduleLocation: 'On Now',
        moduleAction: 'collapse',
        moduleCampaign: 'video discovery row',
      });
    }
  }

  const navigateToPlayer = useCallback(
    (rowId, videoId, rowSlug) => {
      const currentWatchVideo = params.get('video');

      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      if (rowSlug) params.set('rowSlug', rowSlug);

      params.set('isDiscoveryRowVideo', true);

      const search = params.toString();

      hideDiscoveryOverlayonClick();

      if (currentWatchVideo !== params.get('video')) {
        dispatch(exitFullScreenPlayer());
        history.push({
          pathname: '/playback',
          ...(search ? { search } : {}),
        });
      }
    },
    [dispatch, history, params, hideDiscoveryOverlayonClick],
  );

  const onTileClick = useCallback(
    tileProps => {
      const { rowId, video, rowSlug } = tileProps;
      trackEvent('playback interaction', {
        ...getPageValues('main'),
        moduleName: 'discovery row',
        moduleLocation: 'On Now',
        moduleAction: 'click',
        moduleCampaign: 'video discovery row',
      });
      setIsCurrentlyVisible(isOverlayDiscoveryVisible);
      navigateToPlayer(rowId, video.id, rowSlug);
    },
    [navigateToPlayer, trackEvent, isOverlayDiscoveryVisible],
  );

  const onListRowsFocusIndexChanged = useCallback(
    index => {
      onDiscoveryRowFocusIndexChanged(index);
    },
    [onDiscoveryRowFocusIndexChanged],
  );

  const renderRow = useCallback(
    (row, rowIndex) => {
      if (!row.data?.length) return null;

      let focusIdPrefix;
      if (rowIndex === 0) {
        focusIdPrefix = 'firstrow-';
      } else if (rowIndex === liveRow.length - 1) {
        focusIdPrefix = 'lastrow-';
      }

      const updatedRow = { ...row, title: 'On Now' };

      const getHomeRow = () => {
        return (
          <HomeRow
            row={updatedRow}
            displayTitle
            displayScoring={false}
            games={null}
            onTileClick={onTileClick}
            key={updatedRow.id}
            ref={rowIndex === 0 ? firstRowRef : null}
            rowClassName={classNames(styles.row, styles.inverseMargin)}
            titleClassName={classNames(styles.rowTitle)}
            tileClassName={styles.tile}
            focusIdPrefix={focusIdPrefix}
            rowPos={rowIndex + 1}
          />
        );
      };

      return getHomeRow();
    },
    [firstRowRef, onTileClick, liveRow],
  );

  return (
    <React.Fragment>
      {!isLoadingNewVideo && (
        <React.Fragment>
          <div
            className={classNames(
              'w-100',
              'd-flex',
              'flex-column',
              'justify-content-end',
              'align-items-start',
              'overflow-hidden',
              styles.backgroundGradient,
              styles.controlsContainer,
              {
                [styles.hidden]: !isOverlayDiscoveryVisible,
                [styles.visible]: isOverlayDiscoveryVisible,
              },
            )}
          >
            <FocusGroup
              className={classNames(
                'd-flex',
                'flex-row',
                'justify-content-between',
                styles.controls,
              )}
              onActionOverride={onAction}
              horizontal
            >
              <ListRows
                ref={rowsRef}
                focusId={FocusId.DISCOVERY_ROW}
                className={styles.listRows}
                data={liveRow || []}
                renderItem={renderRow}
                buffer={2}
                restorationId={`${FocusId.DISCOVERY_ROW}`}
                onListRowsFocusIndexChanged={onListRowsFocusIndexChanged}
                shouldSnapRowToTop={shouldSnapRowToTop}
                ttsEndOfNavigation={ttsEndOfNavigation}
              />
            </FocusGroup>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

DiscoveryRowOverlay.propTypes = {
  onDiscoveryRowFocusIndexChanged: PropTypes.func,
  firstRowRef: Ref,
  rowsRef: Ref,
  isOverlayDiscoveryVisible: PropTypes.bool,
  onAction: PropTypes.func,
  hideDiscoveryOverlayonClick: PropTypes.func,
};

DiscoveryRowOverlay.defaultProps = {
  onDiscoveryRowFocusIndexChanged: () => {},
  firstRowRef: null,
  rowsRef: null,
  isOverlayDiscoveryVisible: null,
  onAction: () => {},
  hideDiscoveryOverlayonClick: () => {},
};

export default React.forwardRef((props, ref) => (
  <DiscoveryRowOverlay {...props} rowsRef={ref} />
));
